import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { Box, Button, ButtonGroup, Typography } from '@mui/material';
import ViewSidebarOutlined from '@mui/icons-material/ViewSidebarOutlined';
import { useTranslate } from 'react-admin';
import type { Block } from './types';
import { blocksToTiptap, tiptapToBlocks } from './serialization';
import { getBlockEditorExtensions } from './extensions';
import { BlockEditorToolbar } from './BlockEditorToolbar';
import { BlockActionBar } from './BlockActionBar';
import { BlockLibraryPanel } from './BlockLibraryPanel';
import { SlashCommandMenu } from './slash-command/SlashCommandMenu';
import type { BlockMenuItem } from './blockMenuItems';
import { newBlockId } from './blockId';
import './block-editor.css';

export interface BlockEditorProps {
    blocks: Block[];
    onUpdate: (blocks: Block[]) => void;
    editable?: boolean;
    /** Re-mount key: bump when the blocks were replaced from outside (restore, reload). */
    contentKey?: string | number;
    /** Expose the editor instance (e.g. for Ctrl+S or external inserts). */
    onEditorReady?: (editor: Editor | null) => void;
    /** Editor inside a layout block: compact chrome, no layout blocks, no library. */
    nested?: boolean;
}

interface SlashState {
    isOpen: boolean;
    query: string;
    position: { top: number; left: number };
    triggerPos: number;
}

const closedSlash: SlashState = { isOpen: false, query: '', position: { top: 0, left: 0 }, triggerPos: 0 };

/**
 * Block-based lesson editor on TipTap (Autorentool v2, plan §6.7). Blocks map to
 * top-level ProseMirror nodes; `onUpdate` receives the serialised Block[] on
 * every transaction. Type "/" in a text block for the block menu.
 */
export const BlockEditor: React.FC<BlockEditorProps> = ({ blocks, onUpdate, editable = true, onEditorReady, nested = false }) => {
    const translate = useTranslate();
    const onUpdateRef = useRef(onUpdate);
    onUpdateRef.current = onUpdate;
    const bodyRef = useRef<HTMLDivElement>(null);
    const [showLibrary, setShowLibrary] = useState(false);
    const [slash, setSlash] = useState<SlashState>(closedSlash);
    const slashRef = useRef(slash);
    slashRef.current = slash;

    const extensions = useMemo(
        () =>
            getBlockEditorExtensions({
                richtext: translate('blockEditor.placeholder.richtext'),
                heading: translate('blockEditor.placeholder.heading'),
                quote: translate('blockEditor.placeholder.quote'),
                code: translate('blockEditor.placeholder.code'),
            }, { nested }),
        [translate, nested],
    );

    // Computed once on mount: the editor owns the document afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialContent = useMemo(() => blocksToTiptap(blocks), []);

    const editor = useEditor({
        extensions,
        content: initialContent,
        editable,
        onUpdate: ({ editor: ed }) => {
            onUpdateRef.current(tiptapToBlocks(ed.getJSON()));
            if (slashRef.current.isOpen) updateSlashQuery(ed);
        },
        editorProps: {
            attributes: { class: 'oa-block-editor-content', 'aria-label': translate('blockEditor.ariaLabel') },
            handleKeyDown: (view, event) => {
                if (event.key === '/' && !slashRef.current.isOpen) {
                    // #520: remember where the "/" lands NOW — the menu opens on the
                    // next tick, and fast typing (or a paste) could already have put
                    // more characters behind it, which made the trigger position point
                    // into the query and closed the menu again.
                    const triggerPos = view.state.selection.from;
                    setTimeout(() => openSlashMenu(triggerPos), 0);
                    return false;
                }
                if (slashRef.current.isOpen && ['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
                    return true; // handled by the menu's document listener
                }
                if (event.key === 'Escape' && slashRef.current.isOpen) {
                    setSlash(closedSlash);
                    return true;
                }
                return false;
            },
        },
    });

    useEffect(() => {
        onEditorReady?.(editor);
        return () => onEditorReady?.(null);
    }, [editor, onEditorReady]);

    useEffect(() => {
        editor?.setEditable(editable);
    }, [editor, editable]);

    const openSlashMenu = (triggerPos: number) => {
        if (!editor || !bodyRef.current) return;
        const { selection } = editor.state;
        if (selection.from <= triggerPos || editor.state.doc.textBetween(triggerPos, selection.from, '').startsWith('/') === false) return;
        const coords = editor.view.coordsAtPos(selection.from);
        const rect = bodyRef.current.getBoundingClientRect();
        setSlash({
            isOpen: true,
            query: editor.state.doc.textBetween(triggerPos + 1, selection.from, ''),
            position: { top: coords.bottom - rect.top + 4, left: Math.max(0, coords.left - rect.left) },
            triggerPos,
        });
    };

    const updateSlashQuery = (ed: Editor) => {
        const { from } = ed.state.selection;
        const { triggerPos } = slashRef.current;
        if (from <= triggerPos) {
            setSlash(closedSlash);
            return;
        }
        const text = ed.state.doc.textBetween(triggerPos, from, '');
        if (!text.startsWith('/') || text.slice(1).includes(' ')) {
            setSlash(closedSlash);
            return;
        }
        setSlash((prev) => ({ ...prev, query: text.slice(1) }));
    };

    const closeSlash = useCallback(() => setSlash(closedSlash), []);

    const handleSlashSelect = useCallback(
        (item: BlockMenuItem) => {
            // #520: the menu's Enter handler could fire twice (React StrictMode
            // double-invokes updaters; a re-registered listener can see the same
            // key) — the second run deleted the previous block and inserted the
            // node again. Close synchronously via the ref so a repeat is a no-op,
            // and only delete the "/query" text when it is really there.
            if (!editor || !slashRef.current.isOpen) return;
            const { triggerPos } = slashRef.current;
            slashRef.current = closedSlash;
            setSlash(closedSlash);
            const { from } = editor.state.selection;
            const typed = triggerPos >= 0 && from > triggerPos ? editor.state.doc.textBetween(triggerPos, from, '') : '';
            if (typed.startsWith('/')) {
                editor.chain().focus().deleteRange({ from: triggerPos, to: from }).run();
            }
            const pos = editor.state.selection.from;
            editor.chain().focus().insertContentAt(pos, { type: item.nodeType, attrs: { blockId: newBlockId(), ...item.attrs } }).run();
        },
        [editor],
    );

    if (!editor) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 128 }}>
                <Typography variant="body2" color="text.secondary">{translate('blockEditor.loading')}</Typography>
            </Box>
        );
    }

    return (
        <Box className={`oa-block-editor${editable ? '' : ' is-readonly'}${nested ? ' is-nested' : ''}`}>
            <Box sx={{ display: 'flex', minWidth: 0, flex: 1, flexDirection: 'column' }}>
                {editable && !nested && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--oa-divider)', flexWrap: 'wrap' }}>
                        <BlockEditorToolbar editor={editor} onToggleLibrary={() => setShowLibrary((s) => !s)} libraryOpen={showLibrary} />
                        <ButtonGroup size="small" variant="text" sx={{ px: 1 }}>
                            <Button startIcon={<ViewSidebarOutlined />} onClick={() => setShowLibrary((s) => !s)} aria-pressed={showLibrary}>
                                {translate('blockEditor.library.toggle')}
                            </Button>
                        </ButtonGroup>
                    </Box>
                )}
                <Box ref={bodyRef} className="oa-block-editor-body">
                    {editable && <BlockActionBar editor={editor} />}
                    <EditorContent editor={editor} />
                    {editable && <SlashCommandMenu editor={editor} isOpen={slash.isOpen} query={slash.query} position={slash.position} onSelect={handleSlashSelect} onClose={closeSlash} nested={nested} />}
                </Box>
            </Box>
            {editable && !nested && showLibrary && <BlockLibraryPanel editor={editor} onClose={() => setShowLibrary(false)} />}
        </Box>
    );
};

export default BlockEditor;
